// ── Shared presentational components ──
import { useState, useEffect, useRef } from "react";
import { MdEdit, MdDeleteForever, MdVisibility } from "react-icons/md";
import { B, ROLES, SC, SB, SURFACE, SURFACE_MUTED, SURFACE_SUNKEN,
         INK_PRIMARY, INK_MUTED, INK_FAINT, HAIRLINE, HAIRLINE_FIRM } from "./theme";
import { useApp } from "./appContext";
import { useEscape } from "./escape";
export { useEscape };

// A real anchor for in-app page navigation. Left-click routes in-app (no full
// reload); modifier, middle, and right clicks fall through to the browser so
// "open in new tab / new window" and bookmarking work natively. Drop it on a
// row's identifying cell (Loan #, Company) so right-click has a link target.
export const PageLink = ({ page, params, children, style, ...rest }) => {
  const { hrefFor, navClick } = useApp();
  return (
    <a href={hrefFor(page, params)} onClick={(e) => navClick(e, page, params)}
      style={{ color: "inherit", textDecoration: "none", cursor: "pointer", ...style }} {...rest}>
      {children}
    </a>
  );
};

export const Icon = ({ type }) => {
  if (type === "edit") { return <MdEdit title="View" size={15} color={B.cyan} />;};
  if (type === "delete") { return <MdDeleteForever title="View" size={15} color={B.cyan} />;};
  if (type === "view") { return <MdVisibility title="View" size={15} color={B.cyan} />;};
  return null;
};

export const RiskBadge = ({ rating }) => {
  if (!rating) return <span style={{ color:INK_MUTED }}>—</span>;
  const n = parseFloat(rating);
  const color = n <= 5 ? "#0F6E56"    // green — low risk
              : n <= 7 ? "#185FA5"    // blue — moderate
              : n <= 10 ? "#BA7517"    // amber — elevated
              : "#993C1D";            // red — high risk
  const bg    = n <= 5 ? "#E1F5EE"
              : n <= 7 ? "#E6F1FB"
              : n <= 10 ? "#FAEEDA"
              : "#FAECE7";
  return (
    <span style={{ color, background:bg, padding:"2px 10px", borderRadius:4, fontSize:12, fontWeight:600, display:"inline-block" }}>
      {rating}
    </span>
  );
};

export const FInputNum = ({ value, onChange, placeholder, disabled }) => {
  const clean = v => {
    if (v === null || v === undefined || v === "") return "";
    const n = parseFloat(String(v).replace(/,/g, ""));
    return isNaN(n) ? "" : String(n);
  };

  const [display, setDisplay] = useState(() => {
    const c = clean(value);
    if (c === "") return "";
    const n = parseFloat(c);
    return isNaN(n) ? "" : n.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 });
  });
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) {
      const c = clean(value);
      if (c === "") { setDisplay(""); return; }
      const n = parseFloat(c);
      setDisplay(isNaN(n) ? "" : n.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 }));
    }
  }, [value, focused]);

  const handleChange = e => {
    const raw = e.target.value.replace(/,/g, "");
    setDisplay(e.target.value);
    onChange(raw === "" ? "" : raw);
  };

  const handleBlur = () => {
    setFocused(false);
    const c = clean(value);
    if (c === "") { setDisplay(""); return; }
    const n = parseFloat(c);
    setDisplay(isNaN(n) ? "" : n.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 }));
  };

  const handleFocus = () => {
    setFocused(true);
    const c = clean(value);
    setDisplay(c === "" ? "" : c);
  };

  return (
    <input
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder}
      disabled={disabled}
      style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999", background:disabled?"#f0f0f0":"#fff", color:disabled?"#999":"#1a1a1a", textAlign:"left", cursor:disabled?"not-allowed":"auto" }}
    />
  );
};

export const Badge = ({ s }) => <span style={{ color:SC[s]||"#444", background:SB[s]||"#eee", padding:"2px 8px", borderRadius:4, fontSize:12, fontWeight:500, display:"inline-block", whiteSpace:"nowrap" }}>{s}</span>;
export const RoleBadge = ({ role }) => <span style={{ color:ROLES[role]&&ROLES[role].color||"#444", background:ROLES[role]&&ROLES[role].bg||"#eee", padding:"2px 8px", borderRadius:4, fontSize:12, fontWeight:500 }}>{role}</span>;

export const Th = ({ children, style, ...rest }) => <th {...rest} style={{ textAlign:"left", padding:"8px 12px", fontSize:12, fontWeight:500, color:INK_MUTED, borderBottom:`0.5px solid ${HAIRLINE}`, background:SURFACE_MUTED, whiteSpace:"nowrap", ...style }}>{children}</th>;
export const Td = ({ children, style, ...rest }) => <td {...rest} style={{ padding:"9px 12px", fontSize:13, borderBottom:`0.5px solid ${HAIRLINE}`, verticalAlign:"middle", ...style }}>{children}</td>;
export const ThNum = ({ children }) => <th style={{ textAlign:"right", padding:"8px 12px", fontSize:12, fontWeight:500, color:INK_MUTED, borderBottom:`0.5px solid ${HAIRLINE}`, background:SURFACE_MUTED, whiteSpace:"nowrap" }}>{children}</th>;
export const TdNum = ({ children, style, ...rest }) => <td {...rest} style={{ textAlign:"right", padding:"9px 12px", fontSize:13, borderBottom:`0.5px solid ${HAIRLINE}`, verticalAlign:"middle", fontVariantNumeric:"tabular-nums", ...style }}>{children}</td>;

// ── FilterSelect ─────────────────────────────────────────────────────────────
// A labeled dropdown for a toolbar filter. Exists because rows of bare toggle
// buttons don't say what they control — "Open  Done  Dismissed" next to
// "All types  Lead  Opportunity" reads as one undifferentiated row of buttons,
// and you have to click one to find out what it did.
//
// The label is part of the control, the current value is always visible, and an
// active filter gets a cyan border so a narrowed list never looks like an empty
// table.
//
// options: ["A","B"] or [{value,label}]. `hideWhenSingle` drops the whole
// control when the data offers fewer than two choices — a filter that cannot
// change the result is just noise.
export const FilterSelect = ({ label, value, onChange, options, allLabel = "All", hideWhenSingle = false, disabled }) => {
  const opts = (options || []).map(o => (o && typeof o === "object") ? o : { value: o, label: o });
  if (hideWhenSingle && opts.length < 2 && !value) return null;
  return (
    <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13 }}>
      {label && (
        <span style={{ fontSize:11, fontWeight:700, color:INK_MUTED,
                       textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{label}</span>
      )}
      <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        style={{ padding:"6px 10px", borderRadius:8, fontSize:13, cursor: disabled ? "not-allowed" : "pointer",
                 border:"1px solid " + (value ? B.cyan : HAIRLINE_FIRM),
                 background: disabled ? "#f0f0f0" : SURFACE,
                 color: disabled ? "#999" : INK_PRIMARY }}>
        {allLabel !== null && <option value="">{allLabel}</option>}
        {opts.map((o, i) => <option key={String(o.value) + "|" + i} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
};

// data-card marks a card for page-level helpers (the Collateral app's export
// icon finds cards by it). It renders nothing and changes nothing else.
export const Card = ({ children, style, ...rest }) => <div data-card="" {...rest} style={{ background:SURFACE, borderRadius:12, padding:16, boxShadow:"0 2px 12px rgba(0,0,0,0.08)", border:`0.5px solid ${HAIRLINE}`, ...style }}>{children}</div>;
export const ReadOnlyForm = ({ children, readOnly }) => (
  <fieldset disabled={readOnly} style={{ border:"none", padding:0, margin:0, opacity: readOnly ? 1 : 1 }}>
    {readOnly && <div style={{ fontSize:11, color:B.cyanDark, fontWeight:600, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:12, padding:"6px 10px", background:B.cyanLight, borderRadius:6 }}>View Only</div>}
    {children}
  </fieldset>
);
export const StatCard = ({ label, value, sub }) => (
  <div style={{ background:SURFACE_MUTED, borderRadius:"var(--border-radius-md)", padding:"12px 16px" }}>
    <div style={{ fontSize:12, color:INK_MUTED, marginBottom:4 }}>{label}</div>
    <div style={{ fontSize:12, fontWeight:500 }}>{value}</div>
    {sub && <div style={{ fontSize:11, color:INK_MUTED, marginTop:2 }}>{sub}</div>}
  </div>
);
export const Spinner = () => <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:40, color:INK_MUTED, fontSize:13 }}>Loading...</div>;
export const ErrorMsg = ({ msg, onRetry }) => (
  <div style={{ padding:20, background:"#FAECE7", borderRadius:8, border:"0.5px solid #E8B4A8", fontSize:13, color:"#993C1D", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
    <span>⚠️ {msg}</span>
    {onRetry && <button onClick={onRetry} style={{ background:"none", border:"0.5px solid #993C1D", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:12, color:"#993C1D" }}>Retry</button>}
  </div>
);
export const Breadcrumb = ({ crumbs }) => (
  <div style={{ display:"flex", gap:6, fontSize:13, color:INK_MUTED, marginBottom:14, flexWrap:"wrap" }}>
    {crumbs.map((c,i) => <span key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
      {i>0 && <span style={{ opacity:0.4 }}>›</span>}
      <span style={{ color:i===crumbs.length-1?INK_PRIMARY:INK_MUTED, fontWeight:i===crumbs.length-1?500:400 }}>{c}</span>
    </span>)}
  </div>
);
// ── PanelTitle: the title INSIDE a card, as on the account Overview panels ──
// Small cyan capitals over a thin light-cyan rule; an optional count, and
// buttons on the right of the same line. Every card on the account page uses
// it, so a card's name is never floating above the card.
export const PanelTitle = ({ title, count, actions, style }) => (
  <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:12, paddingBottom:6,
                borderBottom:"1px solid "+B.cyanLight, ...style }}>
    <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em" }}>
      {title}{count !== undefined && count !== null && <span style={{ marginLeft:6, color:INK_MUTED, fontWeight:500 }}>({count})</span>}
    </div>
    {actions && <div style={{ marginLeft:"auto", display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>{actions}</div>}
  </div>
);
export const SectionHeader = ({ title, count, actions }) => (
  <div style={{ marginBottom:18, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
    {title&&<h2 style={{ margin:0, fontSize:18, fontWeight:500 }}>{title}</h2>}
    {count !== undefined && <span style={{ fontSize:12, color:INK_MUTED, background:SURFACE_MUTED, padding:"2px 8px", borderRadius:10 }}>{count}</span>}
    <div style={{ marginLeft:"auto", display:"flex", gap:8, flexWrap:"wrap" }}>{actions}</div>
  </div>
);
export const SearchBar = ({ value, onChange, placeholder }) => {
  const ref = useRef();
  return (
    <input
      ref={ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder||"Search..."}
      style={{ padding:"6px 12px", fontSize:13, borderRadius:6, border:`0.5px solid ${HAIRLINE_FIRM}`, background:SURFACE, color:INK_PRIMARY, width:200 }}
    />
  );
};

export const STh = ({ label, sortKey, currentKey, currentDir, onSort, numeric }) => (
  <th onClick={()=>onSort(sortKey)} style={{
    textAlign: numeric?"right":"left", padding:"8px 12px", fontSize:12, fontWeight:500,
    color:INK_MUTED, borderBottom:`0.5px solid ${HAIRLINE}`,
    background:SURFACE_MUTED, whiteSpace:"nowrap",
    cursor:"pointer", userSelect:"none"
  }}>
    <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
      {label}
      <span style={{ fontSize:10, opacity: currentKey===sortKey ? 1 : 0.3 }}>
        {currentKey===sortKey ? (currentDir==='asc'?'▲':'▼') : '▲'}
      </span>
    </span>
  </th>
);

export const Pagination = ({ page, totalPages, total, pageSize, onPage }) => {
  if (totalPages <= 1) return null;
  const start = (page-1)*pageSize + 1;
  const end   = Math.min(page*pageSize, total);
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px", borderTop:`0.5px solid ${HAIRLINE}`, fontSize:12, color:INK_MUTED }}>
      <span>Showing {start}–{end} of {total}</span>
      <div style={{ display:"flex", gap:4 }}>
        <button onClick={()=>onPage(1)} disabled={page===1} style={{ background:"none", border:`0.5px solid ${HAIRLINE_FIRM}`, borderRadius:4, padding:"3px 8px", cursor:page===1?"not-allowed":"pointer", opacity:page===1?0.4:1, fontSize:12 }}>«</button>
        <button onClick={()=>onPage(page-1)} disabled={page===1} style={{ background:"none", border:`0.5px solid ${HAIRLINE_FIRM}`, borderRadius:4, padding:"3px 8px", cursor:page===1?"not-allowed":"pointer", opacity:page===1?0.4:1, fontSize:12 }}>‹</button>
        {/* Page number buttons — show up to 5 around current page */}
        {Array.from({length:totalPages}, (_,i)=>i+1)
          .filter(p => p===1 || p===totalPages || Math.abs(p-page)<=2)
          .reduce((acc,p,i,arr)=>{
            if(i>0 && p-arr[i-1]>1) acc.push('...');
            acc.push(p);
            return acc;
          },[])
          .map((p,i)=> p==='...'
            ? <span key={"ellipsis-"+i} style={{ padding:"3px 6px", fontSize:12 }}>…</span>
            : <button key={p} onClick={()=>onPage(p)} style={{ background:page===p?B.cyan:"none", color:page===p?"#fff":INK_MUTED, border:"0.5px solid "+(page===p?B.cyan:HAIRLINE_FIRM), borderRadius:4, padding:"3px 8px", cursor:"pointer", fontSize:12, fontWeight:page===p?600:400 }}>{p}</button>
          )
        }
        <button onClick={()=>onPage(page+1)} disabled={page===totalPages} style={{ background:"none", border:`0.5px solid ${HAIRLINE_FIRM}`, borderRadius:4, padding:"3px 8px", cursor:page===totalPages?"not-allowed":"pointer", opacity:page===totalPages?0.4:1, fontSize:12 }}>›</button>
        <button onClick={()=>onPage(totalPages)} disabled={page===totalPages} style={{ background:"none", border:`0.5px solid ${HAIRLINE_FIRM}`, borderRadius:4, padding:"3px 8px", cursor:page===totalPages?"not-allowed":"pointer", opacity:page===totalPages?0.4:1, fontSize:12 }}>»</button>
      </div>
    </div>
  );
};

// `title` is passed through so a compact button can carry the long version of
// what it does ("Snooze until 4 Sep") without widening the label.
export const Btn = ({ onClick, children, variant="default", disabled, title }) => {
  // amber is the brand's second colour and had no button treatment at all, so
  // anything wanting it was reaching for a raw <button> and inventing a fourth
  // style. It reads as a real alternative to primary rather than a lesser
  // default — which is what a second CREATE action on a row needs to be.
  //
  // Ink is amberDark, not white: white on #ffb20d is about 1.9:1 and fails
  // outright, while the dark brown clears 7:1. The tokens carry both, so this
  // follows a retune of the palette instead of freezing one hex.
  const s = { default:{ background:"none", border:`0.5px solid ${HAIRLINE_FIRM}`, color:INK_MUTED }, primary:{ background:B.cyan, border:"none", color:"#fff" }, amber:{ background:B.amber, border:"none", color:B.amberDark }, danger:{ background:"none", border:"0.5px solid #E24B4A", color:"#A32D2D" } };
  return <button onClick={onClick} disabled={disabled} title={title} style={{ ...s[variant], padding:"6px 12px", borderRadius:6, cursor:disabled?"not-allowed":"pointer", fontSize:13, fontWeight:500, opacity:disabled?0.45:1 }}>{children}</button>;
};
// Export is a sales manager / admin capability. This wrapper renders nothing
// for a restricted BDO, so the 16 sales pages don't each need to know the rule.
//
// Note this is UX, not the control: the real protection is that a restricted
// user never receives another rep's rows from the API in the first place, so
// there is nothing for a copied fetch call to export either.
export const ExportBtn = ({ onClick, children, disabled }) => {
  const ctx = useApp();
  const role = ctx && ctx.role;
  if (!role || !role.canExportSales) return null;
  return <Btn onClick={onClick} disabled={disabled}>{children || "Export CSV"}</Btn>;
};

export const TabBtn = ({ id, current, label, onClick }) => (
  <button onClick={()=>onClick(id)} style={{
    background: current===id ? B.cyan : SURFACE_MUTED,
    border: "0.5px solid "+(current===id ? B.cyan : HAIRLINE),
    borderBottom: "none",
    borderRadius: "8px 8px 0 0",
    padding: "9px 22px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: current===id ? 600 : 400,
    color: current===id ? "#fff" : INK_MUTED,
    marginBottom: 0,
    transition: "all 0.15s ease",
    position: "relative",
    zIndex: current===id ? 2 : 1,
    boxShadow: current===id ? "none" : "inset 0 -2px 4px rgba(0,0,0,0.04)",
  }}>{label}</button>
);

// Escape closes it, like the × (see ./escape — the top window only).
export const Modal = ({ title, onClose, children, wide, onEdit }) => { useEscape(onClose || null); return (
  <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
    <div style={{ background:"#ffffff", borderRadius:"var(--border-radius-lg)", border:`0.5px solid ${HAIRLINE}`, width:wide?1100:480, maxWidth:"96vw", maxHeight:"88vh", overflowY:"auto" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:`1px solid ${HAIRLINE_FIRM}`, background:"#f8f9fa", borderRadius:"var(--border-radius-lg) var(--border-radius-lg) 0 0" }}>
        <div style={{ fontSize:17, fontWeight:500, color:"#1a1a1a" }}>{title}</div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
         {onEdit && <button onClick={onEdit} style={{ background:"none", border:"none", cursor:"pointer", padding:4, display:"flex" }}>
          <Icon type="edit" />
          </button>}
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#666", lineHeight:1 }}>×</button>
        </div>
      </div>
      <div style={{ padding:24 }}>{children}</div>
    </div>
  </div>
);};
// Off-screen but still read aloud. `display:none` and `visibility:hidden` are
// skipped by screen readers, which is the opposite of what is wanted here.
export const SR_ONLY = {
  position:"absolute", width:1, height:1, padding:0, margin:-1,
  overflow:"hidden", clip:"rect(0 0 0 0)", whiteSpace:"nowrap", border:0,
};

// THE ASTERISK IS NOT THE SIGNAL, IT IS A REMINDER OF THE SIGNAL. It is red and
// it is a glyph, so it carries nothing for anyone not looking at it in colour —
// hence the hidden "(required)" beside it, which is what a screen reader reads.
// The visible star is aria-hidden so the label is not announced as "Expected
// Fund Date star required".
//
// #993C1D is the app's existing error tone, not a pure red: 6.96:1 on white,
// where #ff0000 is 4.0 and reads as an alarm rather than a note.
const REQUIRED_MARK = "#993C1D";

export const Field = ({ label, children, required }) => (
  <div style={{ marginBottom:14 }}>
    <label style={{ display:"block", fontSize:12, fontWeight:500, color:"#555", marginBottom:4 }}>
      {label}
      {required ? (
        <>
          <span aria-hidden="true"
                style={{ color:REQUIRED_MARK, fontWeight:700, marginLeft:3 }}>*</span>
          <span style={SR_ONLY}> (required)</span>
        </>
      ) : null}
    </label>
    {children}
  </div>
);
export const FInput = ({ value, onChange, type="text", placeholder, disabled }) => (
  <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} disabled={disabled} style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999", background:disabled?"#f0f0f0":"#fff", color:disabled?"#999":"#1a1a1a", cursor:disabled?"not-allowed":"auto" }} />
);
export const FSelect = ({ value, onChange, options, disabled }) => (
  <select value={value} onChange={e=>onChange(e.target.value)} disabled={disabled} style={{ width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999", background:disabled?"#f0f0f0":"#fff", color:disabled?"#999":"#1a1a1a", cursor:disabled?"not-allowed":"auto" }}>
    {/* An option is either a bare value ("Active") or {value,label}.
        The old `o.label||o` crashed on {value:"",label:""} — an empty label is
        falsy, so it fell through and tried to render the OBJECT as a React
        child ("Objects are not valid as a React child"). A blank first entry is
        the normal way to spell "no selection", so that was a live crash on any
        form using one. `??` instead of `||` keeps an intentional empty label. */}
    {options.map((o,i)=>{
      const obj = o !== null && typeof o === "object";
      const val = obj ? (o.value ?? "") : (o ?? "");
      const lbl = obj ? (o.label ?? o.value ?? "") : (o ?? "");
      // Index in the key: several options may legitimately share a value ("").
      return <option key={String(val)+"|"+i} value={val}>{lbl}</option>;
    })}
  </select>
);
// Defaults to the delete wording so every existing caller is unchanged.
// Pass title/confirmLabel/variant for an action that isn't a deletion —
// "Confirm delete / Delete" on a button that resets a sync cursor teaches
// people to ignore the dialog, which is worse than having no dialog.
// `acknowledge` turns this into a dead end on purpose: one button, no
// destructive action. It is how a REFUSAL is delivered — the reason a delete
// cannot happen belongs in the dialog the person is already looking at, not in
// a banner somewhere else on the page, and offering them a red Delete button
// underneath a sentence explaining that deleting is impossible would be worse
// than either.
export const Confirm = ({ msg, onConfirm, onCancel, title, confirmLabel, variant, acknowledge }) => (
  <Modal title={title || (acknowledge ? "Cannot delete" : "Confirm delete")} onClose={onCancel}>
    <p style={{ fontSize:14, color:INK_MUTED, marginBottom:20 }}>{msg}</p>
    <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
      {acknowledge
        ? <Btn onClick={onCancel} variant="primary">{confirmLabel || "Close"}</Btn>
        : <>
            <Btn onClick={onCancel}>Cancel</Btn>
            <Btn onClick={onConfirm} variant={variant || "danger"}>{confirmLabel || "Delete"}</Btn>
          </>}
    </div>
  </Modal>
);
